import React, {Fragment, PureComponent} from 'react';
import PropTypes from 'prop-types';
import {connect} from 'react-redux';
import Immutable from 'immutable';

import Button from '../button/Button';
import Icon from '../icon/Icon';
import {getMessage, mapMessageStateToProps} from '../message/Message';
import {CustomPropShapes, errorTypes, svgs, themes} from '../../constants';

import styles from './ErrorView.pcss';
import appActions from '../../redux/actions/app.actions';
import {selectIsLauncher} from '../../redux/selectors/app.selectors';
import WarningTemplate from '../warningTemplate/WarningTemplate';

const PROTOCOL_LAUNCHER = 'com.epicgames.launcher';
const SETTINGS_URL = 'settings';

const mapDispatchToProps = {
    reloadPage: appActions.reloadPage
};

const mapStateToProps = state => ({
    ...mapMessageStateToProps(state),
    isLauncher: selectIsLauncher(state)
});

class ErrorView extends PureComponent {
    static propTypes = {
        staticContext: PropTypes.object,
        status: PropTypes.number,
        messages: PropTypes.instanceOf(Immutable.Map).isRequired,
        reloadPage: PropTypes.func,
        type: CustomPropShapes.oneOfConstant(errorTypes),
        isLauncher: PropTypes.bool,
        showButtons: PropTypes.bool
    };

    getRetryButton = () => {
        const {messages, reloadPage, type} = this.props;

        const isOffline = type === errorTypes.OFFLINE;

        if (isOffline) {
            const btnText = getMessage(messages, 'diesel.common.error.btn.retry');
            return (
                reloadPage && (
                    <Button theme={themes.DARK} onClick={reloadPage}>
                        {btnText}
                    </Button>
                )
            );
        }

        const btnText = getMessage(messages, 'diesel.common.error.btn.back');
        return (
            <Button theme={themes.DARK} to="/">
                {btnText}
            </Button>
        );
    };

    /* eslint-disable react/no-danger */
    getServerSettingsButton = () => {
        const {messages} = this.props;

        const btnText = getMessage(messages, 'diesel.common.error.btn.settings');
        return (
            <Fragment>
                {/* We can't rely on React click handler for the server error,
                    where the app never mounts

                    NOTE This doesn't work in IE,
                    but it only runs in the launcher */}
                <script
                    dangerouslySetInnerHTML={{
                        __html: `
                        document.addEventListener('DOMContentLoaded', function() {
                            document.getElementById('settings').addEventListener('click', function() {
                                window.open('${PROTOCOL_LAUNCHER}://${SETTINGS_URL}');
                            });
                        });
                    `
                    }}
                />
                <button className={styles.settingsButton} id="settings">
                    <Icon ariaLabel={btnText} icon={svgs.SETTINGS} />
                </button>
            </Fragment>
        );
    };

    getButtons({isLauncher, isNotFound, isServerError}) {
        return (
            <React.Fragment>
                {this.getRetryButton()}

                {isLauncher && isServerError && this.getServerSettingsButton()}

                {isNotFound && <span className={styles.character} />}
            </React.Fragment>
        );
    }

    render() {
        const {isLauncher, messages, showButtons = true, status, staticContext, type} = this.props;

        const isServerError = type === errorTypes.SERVER_ERROR;
        const isNotFound = type === errorTypes.NOT_FOUND;

        let statusCode;
        if (isNotFound) statusCode = 404;
        if (isServerError) statusCode = status || 500;

        if (staticContext) {
            staticContext.status = statusCode;
        }

        // NOTE: avoid the Message component since this view can be server rendered
        const title = getMessage(messages, `diesel.common.error.${type}.title`);
        const message = getMessage(messages, `diesel.common.error.${type}.message`);
        const description = getMessage(messages, `diesel.common.error.${type}.description`);

        return (
            <WarningTemplate
                contentBackgroundText={statusCode}
                contentDescription={description}
                contentIcon={!statusCode ? svgs.STRESSED_FACE : null}
                contentMessage={message}
                pageTitle={!isServerError ? title : ''}
                isLauncher={isLauncher}>
                {showButtons && this.getButtons({isLauncher, isNotFound, isServerError})}
            </WarningTemplate>
        );
    }
}

export const ServerErrorView = props => <ErrorView {...props} type={errorTypes.SERVER_ERROR} />;

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(ErrorView);
